"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Categories() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const disciplines = [{
    code: "CAT-001",
    titleStart: "Global",
    titleItalic: "Perspectives",
    description: "Unbiased reporting that bridges borders, bringing you the stories that shape our interconnected world.",
    href: "/NewsroomPage",
    hasImage: true,
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711113/img/global_news_desk.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711113/img/global_news_desk.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711113/img/global_news_desk.png-wvc-sizes",
      alt: "Global news desk with diverse editorial team"
    },
    span: "lg:col-span-7 lg:row-span-2",
    delay: "0ms"
  }, {
    code: "CAT-002",
    titleStart: "Civic",
    titleItalic: "Action",
    description: "Empowering communities through grassroots journalism and actionable insights for local change.",
    href: "/ConnectPage",
    hasImage: false,
    span: "lg:col-span-5 lg:row-span-1",
    delay: "120ms"
  }, {
    code: "CAT-003",
    titleStart: "",
    titleItalic: "Multimedia",
    description: "Documentaries, live streams, and podcasts that amplify underrepresented voices with clarity and depth.",
    href: "/NewsroomPage",
    hasImage: false,
    span: "lg:col-span-5 lg:row-span-1",
    delay: "240ms"
  }];
  return <section data-section-id="19591" id="voices-by-category" ref={sectionRef} className="bg-background text-foreground border-t border-border py-32 md:py-44">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        {}
        <div className="mb-16 md:mb-24 grid grid-cols-12 gap-6" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 520ms cubic-bezier(0.2, 0.7, 0.1, 1), transform 520ms cubic-bezier(0.2, 0.7, 0.1, 1)"
      }}>
          <div className="col-span-12 md:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground">
              {"IVN-00 / Editorial Pillars"}
            </p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-default font-medium leading-[1] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.4vw, 4.25rem)"
          }}>
              Three pillars of{" "}
              <em className="italic font-medium">truth</em>
            </h2>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-2 gap-6 lg:auto-rows-fr">
          {disciplines.map((d, i) => <article key={i} data-index={i} className={`group relative border border-border bg-background p-8 md:p-10 transition-colors duration-[280ms] ease-out hover:border-primary ${d.span} flex flex-col`} style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)",
          transition: `opacity 520ms cubic-bezier(0.2, 0.7, 0.1, 1) ${d.delay}, transform 520ms cubic-bezier(0.2, 0.7, 0.1, 1) ${d.delay}, border-color 280ms ease-out`,
          minHeight: d.hasImage ? "32rem" : "20rem"
        }}>
              {}
              <p className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground">
                {d.code}
              </p>

              {}
              <div className={`mt-10 md:mt-14 flex-1 ${d.hasImage ? "grid grid-cols-1 md:grid-cols-12 gap-8" : ""}`}>
                <div className={d.hasImage ? "md:col-span-8 flex flex-col" : "flex flex-col"}>
                  <h3 className="font-default font-medium leading-[0.95] tracking-tight text-foreground" style={{
                fontSize: d.hasImage ? "clamp(2.75rem, 5.6vw, 5.25rem)" : "clamp(2rem, 3.6vw, 3.5rem)"
              }}>
                    {d.titleStart}
                    <em className="italic font-medium inline-block transition-transform duration-[280ms] ease-out group-hover:translate-x-1">
                      {d.titleItalic}
                    </em>
                  </h3>

                  <p className="mt-8 md:mt-10 text-foreground/80 font-default max-w-[42ch]" style={{
                fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
                lineHeight: 1.5,
                letterSpacing: "-0.005em"
              }}>
                    {d.description}
                  </p>
                </div>

                {}
                {d.hasImage && d.image && <div className="hidden md:block md:col-span-4 self-end">
                    <div className="aspect-[3/2] overflow-hidden">
                      <img src={d.image.src} data-wvc-srcset={d.image.srcset} data-wvc-sizes={d.image.sizes} alt={d.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>
                  </div>}
              </div>

              {}
              <div className="mt-10 flex justify-end">
                <Link to={d.href} className="inline-flex items-center gap-2 font-default text-sm text-foreground border-b border-border pb-1 transition-colors duration-[220ms] ease-out hover:border-primary">
                  <span>Explore coverage</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-[220ms] ease-out group-hover:translate-x-1" />
                </Link>
              </div>

              {}
              <span aria-hidden="true" className="pointer-events-none absolute left-8 md:left-10 top-[5.5rem] h-px w-0 bg-primary transition-[width] duration-[320ms] ease-out group-hover:w-6" />
            </article>)}
        </div>
      </div>
    </section>;
}